import { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import type { Control } from "react-hook-form";
import Select from "react-select";
import type { StylesConfig } from "react-select";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import MiniLoadingSpinner from "../../components/MiniLoadingSpinner";
import Circle from "./Circle";
import type { FC } from "react";

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

export interface LocationSearchMapProps {
  control: Control<any>;
  errors: Record<string, any>;
  setValueForm: (field: string, value: any) => void;
  t: (key: string) => string;
  isEdit?: boolean;
  objYard?: any;
  isLoaded: boolean;
  getValues: (field: string) => any;
  isDarkMode?: boolean;
}

const LocationSearchMap: FC<LocationSearchMapProps> = ({
  control,
  errors,
  setValueForm,
  t,
  isEdit = false,
  objYard,
  isLoaded,
  getValues,
  isDarkMode,
}) => {
  const {
    ready,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete();

  const [centerPosition, setCenterPosition] = useState<{
    lat: number;
    lng: number;
  }>({
    lat: 19.42847,
    lng: -99.12766,
  });
  const [radius, setRadius] = useState<number>(500);
  const [viewState, setViewState] = useState({
    latitude: centerPosition.lat,
    longitude: centerPosition.lng,
    zoom: 10,
  });

  const handleSelect = async (address: string) => {
    setValue(address, false);
    clearSuggestions();
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      setCenterPosition({ lat, lng });
      setViewState({ latitude: lat, longitude: lng, zoom: 10 });
      setValueForm("location", {
        address,
        entryLat: lat,
        entryLng: lng,
        mapLink: `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d${lat}!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s${address}!2s${address}!5e0!3m2!1ses-419!2sar!4v1689881473464!5m2!1ses-419!2sar`,
      });
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  const handleViewChange = (newViewState: any) => {
    if (
      typeof newViewState?.latitude === "number" &&
      typeof newViewState?.longitude === "number" &&
      typeof newViewState?.zoom === "number" &&
      isFinite(newViewState.latitude) &&
      isFinite(newViewState.longitude)
    ) {
      setViewState(newViewState);
    }
  };

  const changeCenter = (newCenter: any) => {
    if (!newCenter) return;
    setCenterPosition({ lng: newCenter.lng(), lat: newCenter.lat() });
  };

  useEffect(() => {
    if (isEdit && objYard) {
      const { location } = objYard;
      if (
        location?.address &&
        location?.entryLatitude &&
        location?.entryLongitude &&
        location?.centerLatitude &&
        location?.centerLongitude
      ) {
        if (location?.entryLatitude && location?.entryLongitude) {
          setCenterPosition({
            lat: location?.centerLatitude,
            lng: location?.centerLongitude,
          });
          setViewState({
            latitude: location?.centerLatitude,
            longitude: location?.centerLongitude,
            zoom: 15,
          });
          setRadius(location?.radius);
        } else {
          setCenterPosition({ lat: location.lat, lng: location.lng });
          setViewState({
            latitude: location.lat,
            longitude: location.lng,
            zoom: 15,
          });
          setRadius(500);
        }
      } else {
        navigator.geolocation.getCurrentPosition(
          (currentPosition) => {
            const { latitude, longitude } = currentPosition.coords;
            setCenterPosition({ lat: latitude, lng: longitude });
            setViewState((vs) => ({ ...vs, latitude, longitude, zoom: 10 }));
          },
          (error) => {
            console.log(error);
          },
          { enableHighAccuracy: true }
        );
      }
    } else {
      navigator.geolocation.getCurrentPosition(
        (currentPosition) => {
          const { latitude, longitude } = currentPosition.coords;
          setCenterPosition({ lat: latitude, lng: longitude });
          setViewState((vs) => ({ ...vs, latitude, longitude, zoom: 10 }));
        },
        (error) => {
          console.log(error);
        },
        { enableHighAccuracy: true }
      );
    }
  }, []);

  useEffect(() => {
    const location = getValues("location");
    setValueForm("location", {
      ...location,
      centerLat: centerPosition.lat,
      centerLng: centerPosition.lng,
      radius,
    });
  }, [centerPosition, radius]);

  const options =
    status === "OK"
      ? data.map(({ description }) => ({
          value: description,
          label: description,
        }))
      : [];

  const customStyles: StylesConfig<any, false> = {
    control: (provided) => ({
      ...provided,
      cursor: "pointer",
      width: "100%",
      height: "2.75rem",
      border: errors?.location
        ? "2px solid #991b1b"
        : isDarkMode
        ? "2px solid #4A4A4A"
        : "2px solid #DFE5EF",
      borderRadius: "0.5rem",
      boxShadow: "none",
      fontSize: "15px",
      marginLeft: 0,
      color: isDarkMode ? "#fff" : "#0C0A09",
      backgroundColor: isDarkMode ? "#232323" : "#fff",
      "&:hover": {
        borderColor: errors.location
          ? "#991b1b"
          : isDarkMode
          ? "#1C93FF"
          : "#1C93FF",
      },
      "&:focus": {
        borderColor: errors.location
          ? "#991b1b"
          : isDarkMode
          ? "#1C93FF"
          : "#1C93FF",
      },
    }),
    option: (provided, data) => ({
      ...provided,
      cursor: "pointer",
      fontSize: "15px",
      color: isDarkMode ? "#fff" : "#0C0A09",
      backgroundColor: data.isFocused
        ? isDarkMode
          ? "#232323"
          : "#F3F4F6"
        : isDarkMode
        ? "#232323"
        : undefined,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#232323" : "#fff",
      borderRadius: "0.5rem",
      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#0C0A09",
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? "#fff" : "#0C0A09",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? "#bdbdbd" : "#616161",
    }),
  };

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="flex flex-col gap-3 w-full h-[27.5rem]">
        <Controller
          name="location"
          control={control}
          rules={{ required: t("requiredField") }}
          render={({ field }) => (
            <Select
              {...field}
              id="location"
              name="location"
              value={options.find((option) => option.value === field.value)}
              onChange={(option: any) => handleSelect(option.value)}
              onInputChange={(direction: string) => setValue(direction)}
              options={options}
              isSearchable
              placeholder={t("placeholderLocationYard")}
              isDisabled={!ready}
              styles={customStyles}
              noOptionsMessage={() => null}
              defaultInputValue={isEdit ? objYard?.location?.address : ""}
            />
          )}
        />
        {errors?.location && (
          <p className="text-red-800 text-sm text-center">
            {t(errors?.location?.message)}
          </p>
        )}
        <h2 className="text-xs text-gray-400 pl-2">
          {t("instructionMapTruckYard")}
        </h2>
        <div className="w-full h-full">
          {isLoaded ? (
            <Map
              viewState={viewState}
              onCameraChanged={handleViewChange}
              mapId={"86bc5ef6ec400969"}
              className="w-full h-full rounded-lg border-4 border-[#1C93FF]"
            >
              <Marker
                position={centerPosition}
                title={"Arrastre hasta el centro del patio de maniobras"}
                draggable
                onDragEnd={(event: any) => {
                  setCenterPosition({
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng(),
                  });
                }}
              />
              <Circle
                radius={radius}
                center={centerPosition}
                onRadiusChanged={setRadius}
                onCenterChanged={changeCenter}
                strokeColor={"#1C93FF"}
                strokeOpacity={1}
                strokeWeight={3}
                fillColor={"#1C93FF"}
                fillOpacity={0.3}
                editable
                draggable
              />
            </Map>
          ) : (
            <div className="flex items-center justify-center w-full h-full rounded-lg border-4 border-[#1C93FF]">
              <MiniLoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </APIProvider>
  );
};

export default LocationSearchMap;
